import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Dimensions, ScrollView, StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'trpc-react-native-ui';
import theme from '../../../theme-config';
import TopUpDoughnut from '../TopUpDoughnut';
import UsageDoughnut from '../UsageDoughnut';

const containerMargin = 8;
const innerContainerPadding = 15;
var containerWidth = Dimensions.get('window').width;
containerWidth -= containerMargin * 2;
containerWidth -= innerContainerPadding * 2;
const doughnutSize = containerWidth / 4;

const styles = StyleSheet.create({
  usagesContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: innerContainerPadding,
    paddingBottom: innerContainerPadding,
  },
  usage: {
    width: doughnutSize + 20, // enough to show "out of 100 sms" on one line
  },
  scrollableUsagesContainer: {
    width: '100%',
    paddingBottom: innerContainerPadding,
  },
  scrollableUsagesContentContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: innerContainerPadding,
  },
  contentView: {
    height: doughnutSize * 2,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const formatUsageDoughnutLabel = (label) => {
  if (/^ROA_DATA_ROA{1,2}\d_CREDIT$/.test(label)) {
    return 'ROA_DATA_CREDIT';
  }
  return label;
};

export default class UsageSwiper extends Component {
  shouldComponentUpdate(nextProps) {
    return (
      !isEqual(this.props.usages, nextProps.usages) ||
      this.props.activeUsage !== nextProps.activeUsage
    );
  }

  _renderUsageDoughnut(usage, id) {
    const active =
      this.props.activeUsage && this.props.activeUsage === usage.label;
    return (
      <UsageDoughnut
        key={id}
        current={usage.currentValue}
        max={usage.maxValue}
        unitType={usage.unitType}
        label={formatUsageDoughnutLabel(usage.label)}
        size={doughnutSize}
        isActive={active}
        onPress={() => this.props.onUsagePress(usage)}
        style={styles.usage}
      />
    );
  }

  _renderTopUpDoughnut(usage, id) {
    const active =
      this.props.activeUsage && this.props.activeUsage === usage.label;
    return (
      <TopUpDoughnut
        key={id}
        current={usage.currentValue}
        unitType={usage.unitType}
        label={usage.label}
        size={doughnutSize}
        isActive={active}
        endDateTime={usage.endDateTime}
        onPress={() => this.props.onUsagePress(usage)}
        style={styles.usage}
      />
    );
  }

  _sortStandardDoughnut = usages => {
    let data, voice, sms, dataAndEurope;
    const standardsDoughnuts = theme.doughnutConfig.standardDoughnut;

    for (const usage of usages) {
      for (const standardDoughnut of standardsDoughnuts) {
        if (usage.label === standardDoughnut) {
          if (
            standardDoughnut.includes('DATA') &&
            standardDoughnut.includes('EUROPE')
          ) {
            dataAndEurope = usage;
            break;
          } else if (standardDoughnut.includes('VOICE')) {
            voice = usage;
            break;
          } else if (standardDoughnut.includes('SMS')) {
            sms = usage;
            break;
          } else if (standardDoughnut.includes('DATA')) {
            data = usage;
            break;
          }
        }
      }

      if (data && voice && dataAndEurope && sms)
        return [data, voice, dataAndEurope, sms];
    }

    return [data, voice, dataAndEurope, sms].filter(Boolean);
  };

  _sortIddDoughnuts = usages => {
    let iddVoice, iddVoiceRDM1, iddVoiceRDM2;
    const iddDoughnuts = theme.doughnutConfig.iddDoughnut;

    for (const usage of usages) {
      for (const iddDoughnut of iddDoughnuts) {
        if (usage.label === iddDoughnut) {
          if (iddDoughnut.includes('IDD') && iddDoughnut.includes('RDM1')) {
            iddVoiceRDM1 = usage;
          } else if (
            iddDoughnut.includes('IDD') &&
            iddDoughnut.includes('RDM2')
          ) {
            iddVoiceRDM2 = usage;
          } else if (iddDoughnut.includes('IDD')) {
            iddVoice = usage;
          }
        }
      }
      if (iddVoiceRDM1 && iddVoiceRDM2 && iddVoice) {
        return [iddVoice, iddVoiceRDM1, iddVoiceRDM2];
      }
    }

    return [iddVoice, iddVoiceRDM1, iddVoiceRDM2].filter(Boolean);
  };

  _sortUsageCategory = (usages, doughnutCategory) =>
    theme.doughnutConfig[doughnutCategory]
      .map(usageLabel => usages.find(usage => usage.label === usageLabel))
      .filter(Boolean);

  _sortAndRenderUsages = usages => {
    const sortedStandardDoughnuts = this._sortStandardDoughnut(usages);
    const sortedIddDoughnuts = this._sortIddDoughnuts(usages);
    const sortedExtraCreditDoughnuts = this._sortUsageCategory(
      usages,
      'extraCreditDoughnut',
    );
    const unsortedDoughnuts = usages.filter(
      usage =>
        !sortedStandardDoughnuts
          .concat(sortedIddDoughnuts)
          .concat(sortedExtraCreditDoughnuts)
          .map(u => u.label)
          .includes(usage.label) &&
        !usage.label.includes('ROAMING') &&
        !usage.label.includes('RLH'),
    );

    return sortedStandardDoughnuts
      .map(usage => this._renderUsageDoughnut(usage, usage.label))
      .concat(
        sortedExtraCreditDoughnuts.map(usage =>
          this._renderTopUpDoughnut(usage, usage.label),
        ),
      )
      .concat(
        sortedIddDoughnuts.map(usage =>
          this._renderTopUpDoughnut(usage, usage.label),
        ),
      )
      .concat(
        unsortedDoughnuts.map(usage =>
          this._renderUsageDoughnut(usage, usage.label),
        ),
      );
  };

  render() {
    const { usages } = this.props;
    if (!usages) {
      return <ActivityIndicator style={styles.contentView} />;
    }

    const sortedUsages = this._sortAndRenderUsages(usages);
    if (sortedUsages.length < 4) {
      return <View style={styles.usagesContainer}>{sortedUsages}</View>;
    }

    return (
      <ScrollView
        horizontal
        style={styles.scrollableUsagesContainer}
        contentContainerStyle={[
          styles.scrollableUsagesContentContainer,
          {
            width:
              sortedUsages.length * (doughnutSize + 2 * innerContainerPadding),
          },
        ]}>
        {sortedUsages}
      </ScrollView>
    );
  }
}

UsageSwiper.propTypes = {
  usages: PropTypes.arrayOf(PropTypes.object),
  activeUsage: PropTypes.string,
  onUsagePress: PropTypes.func,
};
